import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, FormField, FormHelp, Label, Textarea, TextButton } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/rental-manager-env';

import {
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexUpdate,
} from '../../../hooks/queries/multiplex';
import { useGetListingDetails, useGetListingRecommendations } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';
import { getFirstUnitError } from '../../../utils/multiplexUpdateErrorUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { RECS } from '../../../constants/RecommendationsConstants';

import PropertyDescriptionStrength from '../../../../srcLegacy/shared/components/postingPath/PropertyDescriptionStrength';
import RecommendationModal from '../../modals/RecommendationModal';

const apiFieldName = 'fullDescription';

const PropertyDescriptionFormField = ({ isRelistingFieldset = false }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });
  const { data: multiplexData } = useMultiplexDetails({ enabled: !isOnReviewStep });
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const recommendationTypes = new Set(recommendationsData?.recommendations.map((r) => r.type) ?? []);
  const initialDescription =
    !isOnReviewStep && multiplexData?.fullDescription
      ? multiplexData.fullDescription
      : (listingDetails?.fullDescription ?? '');

  const label = isRoomForRent ? 'Room and shared space description' : 'Property description';
  const inputName = isRoomForRent ? 'roomDescription' : 'description';
  const isRequired = isRoomForRent;

  const [multiplexError, setMultiplexError] = useState<string | undefined>(undefined);
  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const updateMutation = useMultiEditMutation();

  const descriptionField = useFieldSave({
    fieldName: inputName,
    apiFieldName: 'fullDescription',
    initialValue: initialDescription,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        property: {
          listingAlias,
          fullDescription: value,
        },
      },
    }),
  });

  const { mutateAsync: multiplexUpdateAsync } = useMultiplexUpdate({
    meta: {
      context: {
        api: 'useMultiplexUpdate',
        details: 'PropertyDescriptionFormField',
        fieldName: apiFieldName,
      },
    },
  });

  const handleBlur = async () => {
    setMultiplexError(undefined);

    if (isOnReviewStep || !isInMultiplexListingFlow) {
      descriptionField.onBlur?.();
      return;
    }

    if (!multiplexBuildingSessionId) {
      logError({
        error: new Error('Unable to save: missing multiplexBuildingSessionId'),
        context: { details: 'PropertyDescriptionFormField.handleBlur', fieldName: apiFieldName },
      });
      return;
    }

    try {
      const response = await multiplexUpdateAsync({
        getParams: { multiplexBuildingSessionId },
        postData: { fullDescription: descriptionField.value },
      });

      const unitErrorMessage = getFirstUnitError(response.units, {
        fieldName: apiFieldName,
        fallbackMessage: 'Unable to save description',
        responseFailed: response.success === false,
      });

      if (unitErrorMessage) {
        setMultiplexError(unitErrorMessage);
      }
    } catch (err) {
      const message = DatarouterError.is(err)
        ? DatarouterError.getDisplayMessage(err)
        : ((err as { message?: string })?.message ?? '');
      setMultiplexError(message);
    }
  };

  const displayError = multiplexError ?? descriptionField.error;

  return (
    <div data-id="PropertyDescriptionFormField">
      <FormField
        error={!!displayError}
        description={
          displayError && (
            <FormHelp>
              <span dangerouslySetInnerHTML={{ __html: displayError }} />
            </FormHelp>
          )
        }
        required={isRequired}
        label={
          isRelistingFieldset ? (
            <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
              <Label>{label}</Label>
              {recommendationTypes.has(isRoomForRent ? RECS.ROOM_DESCRIPTION : RECS.DESCRIPTION) && (
                <TextButton
                  textStyle="body-sm-bold"
                  onClick={() => {
                    setDisplayRecsModal(true);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          ) : (
            <Label>{label}</Label>
          )
        }
      >
        <Textarea
          name={inputName}
          value={descriptionField.value}
          rows={10}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => descriptionField.onChange(e.target.value)}
          onBlur={handleBlur}
          data-testid="PropertyDescriptionFormField-Textarea"
        />
        {Boolean(!displayError && descriptionField.value) && (
          <PropertyDescriptionStrength description={descriptionField.value} />
        )}
      </FormField>
      {isRelistingFieldset && displayRecsModal && (
        <RecommendationModal onClose={() => setDisplayRecsModal(false)} recommendationType={RECS.DESCRIPTION} />
      )}
    </div>
  );
};

export default PropertyDescriptionFormField;
